import { ScrollView, XStack, YStack, useTheme } from "tamagui";

import { PartnerIntent } from "@/api/generated/models";
import { useGetUserProfile } from "@/api/queries/";
import ConversationText2Outline from "@/assets/icons/ConversationText2Outline";
import DigitalHealth from "@/assets/icons/DigitalHealth";
import DoctorOutline from "@/assets/icons/DoctorOutline";
import Workwhile from "@/assets/images/workwhile-logo.png";
import {
  Button,
  Footer,
  Header,
  Heading,
  Image,
  Paragraph,
  Text,
} from "@/components";
import AffiliateImageTopBanner from "@/components/templates/dialog/banners/AffiliateImageTopBanner";
import { brandName } from "@/config/brand";

const partnerLogoMap: Partial<
  Record<string, { source: any; width: number; height: number }>
> = {
  [PartnerIntent.workwhile]: { source: Workwhile, width: 68, height: 12 },
};

interface AffiliateWelcomeScreenProps {
  onContinue: () => void;
  // partnerUid drives co-brand logo selection. Sourced from the /affiliate
  // response's newly_affiliated_partner_uids, NOT userProfile.partnerIntent —
  // those can diverge when broad-match claims a partner different from what
  // the user originally signed up against (e.g. FIDO-361: Walmart sign-up,
  // WorkWhile claim on sign-in).
  partnerUid?: string;
}

function PartnerLogo({ partner }: { partner?: string }) {
  const partnerLogo = partner ? partnerLogoMap[partner] : undefined;
  if (!partnerLogo) return null;

  return (
    <Image
      source={partnerLogo.source}
      width={partnerLogo.width}
      height={partnerLogo.height}
      $lg={{ width: 103, height: 19 }}
    />
  );
}

export default function AffiliateWelcomeScreen({
  onContinue,
  partnerUid,
}: AffiliateWelcomeScreenProps) {
  const { data: userProfile } = useGetUserProfile();
  const firstName = userProfile?.firstName;
  const theme = useTheme();
  const iconColor = theme.iconBase?.get();

  return (
    <>
      <AffiliateImageTopBanner
        title={`Free ${brandName} membership`}
        partnerLogo={<PartnerLogo partner={partnerUid} />}
      />
      <ScrollView
        flex={1}
        contentContainerStyle={{
          paddingHorizontal: "$250",
          paddingTop: "$250",
          paddingBottom: "$200",
        }}
      >
        <Header gap="$150">
          <Heading size="$xl">
            Welcome{firstName ? ` ${firstName}` : ""}!
          </Heading>
          <Paragraph size="$m" color="$textSecondary">
            Here's what's included in your membership.
          </Paragraph>
        </Header>

        <YStack gap="$200" marginTop="$250">
          <XStack gap="$200" alignItems="center">
            <ConversationText2Outline color={iconColor} />
            <Text size="$sm">
              <Text size="$sm" color="$textLink">
                Ask {brandName}'s AI assistant
              </Text>{" "}
              any health question.
            </Text>
          </XStack>
          <XStack gap="$200" alignItems="center">
            <DoctorOutline color={iconColor} />
            <Text size="$sm">
              <Text size="$sm" color="$textLink">
                Connect with licensed clinicians
              </Text>{" "}
              for care.
            </Text>
          </XStack>
          <XStack gap="$200" alignItems="center">
            <DigitalHealth color={iconColor} />
            <Text size="$sm">
              <Text size="$sm" color="$textLink">
                Manage your health
              </Text>{" "}
              all in one place.
            </Text>
          </XStack>
        </YStack>
      </ScrollView>

      <Footer mode="dialog" padding="$200">
        <Button
          mode="primary"
          constrained
          onPress={onContinue}
          dataTrackingId="affiliate-welcome-continue"
        >
          Continue
        </Button>
      </Footer>
    </>
  );
}
